class Upsell extends HTMLElement {
  constructor() {
    super();

    this.ENABLE_VARIANT_SYNC = false;
    this.formId = this.dataset.formId;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.stickyForms = this.getStickyForms();
    this.quantityInputsByIndex = new Map();
    this.idInputsByIndex = new Map();
    this.onStorageUpdatedBound = this.syncBadgesWithInputs.bind(this);
    this.variantChangeTimeout = null;
    this.initialSyncTimeout = null;
    this.onVariantChangeBound = null;
    this.onProductRerenderBound = null;
    this.handleFormSubmitBound = null;
    this.ownsFormSubmit = false;

    this.createMissingHiddenInputs();
    this.refreshInputCaches();
    this.init();
    this.setupVariantMatching();
    this.setupBadgeSupport();
    this.setupAddToCartIntercept();
    this.setupFormReattach();
  }

  disconnectedCallback() {
    if (this.handleFormSubmitBound) {
      document.removeEventListener("submit", this.handleFormSubmitBound, true);
      this.handleFormSubmitBound = null;
      this.ownsFormSubmit = false;
    }

    document.removeEventListener(
      "upsell:storage-updated",
      this.onStorageUpdatedBound
    );

    if (this.onVariantChangeBound) {
      document.removeEventListener("variant:change", this.onVariantChangeBound);
      this.onVariantChangeBound = null;
    }

    if (this.onProductRerenderBound) {
      document.removeEventListener(
        "product:rerender",
        this.onProductRerenderBound
      );
      this.onProductRerenderBound = null;
    }

    if (this.onProductRerenderForFormBound) {
      document.removeEventListener(
        "product:rerender",
        this.onProductRerenderForFormBound
      );
      this.onProductRerenderForFormBound = null;
    }

    if (this.initialSyncTimeout) {
      clearTimeout(this.initialSyncTimeout);
      this.initialSyncTimeout = null;
    }

    if (this.variantChangeTimeout) {
      clearTimeout(this.variantChangeTimeout);
      this.variantChangeTimeout = null;
    }
  }

  init() {
    this.querySelectorAll("input[name='upsell']").forEach((checkbox) => {
      const productKey = checkbox.dataset.upsellProductKey;
      let slotKey = null;
      if (productKey) {
        const hiddenIdInput = this.querySelector(
          `input[data-upsell-item][data-input-id="${productKey}"]`
        );
        slotKey = hiddenIdInput?.dataset.upsellItem || null;
      }
      if (!slotKey) return;
      checkbox.dataset.index = slotKey;
      checkbox.addEventListener("change", (event) => {
        this.handleChange(event.target);
      });

      this.updateQuantityForAllForms(slotKey, checkbox.checked);
    });

    this.querySelectorAll("[data-upsell-matcher]").forEach((upsellItem) => {
      upsellItem.addEventListener("click", (event) => {
        if (this.shouldIgnoreUpsellClick(event.target)) {
          return;
        }

        const checkbox = upsellItem.querySelector("input[name='upsell']");
        if (!checkbox) return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  shouldIgnoreUpsellClick(target) {
    return !!target.closest("button, a, input, label, select, textarea");
  }

  setupBadgeSupport() {
    document.addEventListener("upsell:storage-updated", this.onStorageUpdatedBound);

    // Wait for the rest of the components to finish hydrating before the first sync.
    this.initialSyncTimeout = setTimeout(() => {
      this.syncBadgesWithInputs();
      this.initialSyncTimeout = null;
    }, 250);
  }

  syncBadgesWithInputs() {
    const addedProducts = this.getAddedProductsFromBadges();
    const addedProductIds = new Set(addedProducts.map((item) => item.productId));

    addedProducts.forEach((item) => {
      const { variantId, index } = item;

      const idInputs = this.idInputsByIndex.get(String(index)) || [];
      idInputs.forEach((input) => {
        if (variantId) {
          input.value = variantId;
        }
      });

      this.updateQuantityForAllForms(index, true);
    });

    const allCheckboxes = this.querySelectorAll("input[name='upsell']");
    allCheckboxes.forEach((checkbox, index) => {
      const productId = checkbox.closest("[data-product-id]")?.dataset.productId;
      const hasBadge = productId ? addedProductIds.has(productId) : false;

      if (!hasBadge && !checkbox.checked) {
        this.updateQuantityForAllForms(index, false);
      }
    });
  }

  getAddedProductsFromBadges() {
    const STORAGE_KEY = "upsell_added_products";
    let addedProductIds = [];

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) {
        addedProductIds = parsed;
      }
    } catch (e) {
      console.error("Error reading localStorage:", e);
    }

    const result = [];

    addedProductIds.forEach((productId) => {
      const badge = this.querySelector(
        `[data-badge-type="added"][data-product-id="${productId}"][data-is-added="true"]`
      );
      if (!badge) {
        return;
      }

      const upsellItem = badge.closest("[data-current-variant-id]");
      if (!upsellItem) {
        return;
      }

      const variantId = upsellItem.dataset.currentVariantId;
      const checkbox = upsellItem.querySelector('input[name="upsell"]');
      const index = checkbox ? checkbox.dataset.index : null;

      if (variantId && index !== null) {
        result.push({
          productId,
          variantId,
          index: parseInt(index, 10),
        });
      }
    });

    return result;
  }

  handleChange(target) {
    const checked = target.checked;
    const slotKey = target.dataset.index;
    const productKey = target.dataset.upsellProductKey;

    if (slotKey) {
      this.updateQuantityForAllForms(slotKey, checked);
    }

    if (!productKey) return;
    const siblings = document.querySelectorAll(
      `input[data-upsell-product-key="${productKey}"]`
    );
    siblings.forEach((sibling) => {
      if (sibling === target || sibling.checked === checked) return;
      sibling.checked = checked;
      sibling.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  getStickyForms() {
    if (!this.productId || !this.sectionId) {
      return [];
    }

    const stickyFormId = `product-form-sticky-${this.productId}-${this.sectionId}`;
    const stickyForm = document.getElementById(stickyFormId);
    return stickyForm ? [stickyForm] : [];
  }

  getManagedForms() {
    const forms = [];
    const primaryForm = document.getElementById(this.formId);
    if (primaryForm) {
      forms.push(primaryForm);
    }

    this.stickyForms = this.getStickyForms();
    this.stickyForms.forEach((form) => {
      if (!forms.includes(form)) {
        forms.push(form);
      }
    });

    return forms;
  }

  getManagedInputsByName(name) {
    const inputs = Array.from(
      document.querySelectorAll(`input[name="${name}"][form="${this.formId}"]`)
    );

    this.stickyForms = this.getStickyForms();
    this.stickyForms.forEach((stickyForm) => {
      inputs.push(...stickyForm.querySelectorAll(`input[name="${name}"]`));
    });

    return inputs;
  }

  syncManagedQuantitiesFromCheckboxes() {
    this.querySelectorAll("input[name='upsell']").forEach((checkbox, index) => {
      this.updateQuantityForAllForms(index, checkbox.checked);
    });
  }

  createMissingHiddenInputs() {
    const sourceIdInputs = this.querySelectorAll("input[data-upsell-item]");
    const sourceQuantityInputs = this.querySelectorAll("input[data-upsell-quantity]");

    this.stickyForms = this.getStickyForms();
    this.stickyForms.forEach((stickyForm) => {
      sourceIdInputs.forEach((sourceInput) => {
        const slotKey = sourceInput.dataset.upsellItem;
        if (!slotKey) return;
        const inputName = `items[${slotKey}][id]`;
        if (stickyForm.querySelector(`input[name="${inputName}"]`)) return;
        const idInput = document.createElement("input");
        idInput.type = "hidden";
        idInput.name = inputName;
        idInput.value = sourceInput.value;
        idInput.dataset.upsellItem = slotKey;
        if (sourceInput.dataset.inputId) {
          idInput.dataset.inputId = sourceInput.dataset.inputId;
        }
        stickyForm.appendChild(idInput);
      });

      sourceQuantityInputs.forEach((sourceInput) => {
        const slotKey = sourceInput.dataset.upsellQuantity;
        if (!slotKey) return;
        const inputName = `items[${slotKey}][quantity]`;
        if (stickyForm.querySelector(`input[name="${inputName}"]`)) return;
        const quantityInput = document.createElement("input");
        quantityInput.type = "hidden";
        quantityInput.name = inputName;
        quantityInput.value = "0";
        quantityInput.dataset.upsellQuantity = slotKey;
        stickyForm.appendChild(quantityInput);
      });
    });
  }

  refreshInputCaches() {
    this.quantityInputsByIndex.clear();
    this.idInputsByIndex.clear();

    const slotKeys = new Set();
    this.querySelectorAll("input[data-upsell-item]").forEach((input) => {
      if (input.dataset.upsellItem) slotKeys.add(input.dataset.upsellItem);
    });

    slotKeys.forEach((slotKey) => {
      document
        .querySelectorAll(`input[name="items[${slotKey}][id]"]`)
        .forEach((input) => this.cacheInput(this.idInputsByIndex, slotKey, input));

      document
        .querySelectorAll(`input[name="items[${slotKey}][quantity]"]`)
        .forEach((input) => this.cacheInput(this.quantityInputsByIndex, slotKey, input));
    });
  }

  cacheInput(cacheMap, index, input) {
    const key = String(index);
    if (!cacheMap.has(key)) {
      cacheMap.set(key, []);
    }

    cacheMap.get(key).push(input);
  }

  updateQuantityForAllForms(index, checked) {
    const key = String(index);

    // Defense-in-depth: if every checkbox sharing this slot's product key is
    // inside a CSS-hidden item (e.g., the desktop product_limit nth-child
    // rule hid item 3 because desktop_limit=2), force-disable the inputs
    // regardless of `checked`. handleFormSubmit also filters by visibility,
    // but the disabled flag is what protects us if the submit intercept
    // ever fails and the form posts natively.
    let anyVisible = false;
    const refCheckbox = this.querySelector(
      `input[name='upsell'][data-index="${index}"]`
    );
    const productKey = refCheckbox?.dataset.upsellProductKey;
    if (productKey) {
      // Look across ALL roots — a sticky-bar duplicate may still be visible
      // even when the inline block hides this slot.
      document
        .querySelectorAll(
          `input[name='upsell'][data-upsell-product-key="${productKey}"]`
        )
        .forEach((cb) => {
          if (Upsell.isCheckboxVisible(cb)) anyVisible = true;
        });
    } else if (refCheckbox) {
      anyVisible = Upsell.isCheckboxVisible(refCheckbox);
    } else {
      // No checkbox reference (rare) — fall back to legacy behavior
      anyVisible = true;
    }

    const effective = checked && anyVisible;
    const quantityValue = effective ? "1" : "0";
    let quantityInputs = this.quantityInputsByIndex.get(key) || [];

    if (quantityInputs.length === 0) {
      const quantityInputName = `items[${index}][quantity]`;
      quantityInputs = this.getManagedInputsByName(quantityInputName);
      this.quantityInputsByIndex.set(key, quantityInputs);
    }

    quantityInputs.forEach((input) => {
      input.value = quantityValue;
      input.disabled = !effective;
    });

    // Also disable/enable the ID inputs so they are excluded from FormData
    let idInputs = this.idInputsByIndex.get(key) || [];
    if (idInputs.length === 0) {
      const idInputName = `items[${index}][id]`;
      idInputs = this.getManagedInputsByName(idInputName);
      this.idInputsByIndex.set(key, idInputs);
    }
    idInputs.forEach((input) => {
      input.disabled = !effective;
    });
  }

  /**
   * Returns true if a checkbox sits inside a visible upsell item (not hidden
   * by CSS rules like the desktop/mobile product_limit nth-child filter).
   * Use as the single source of truth for "should this checkbox count?"
   * across handleFormSubmit and updateQuantityForAllForms.
   */
  static isCheckboxVisible(checkbox) {
    if (!checkbox) return false;
    const item = checkbox.closest("[data-upsell-matcher]");
    if (!item) return true; // checkbox outside the item — can't determine, default visible
    return getComputedStyle(item).display !== "none";
  }

  setupVariantMatching() {
    if (!this.ENABLE_VARIANT_SYNC) {
      return;
    }

    const initialOptions = this.getCurrentMainProductOptions();
    if (initialOptions.length > 0) {
      this.updateUpsellVariantsLightweight(initialOptions);
    }

    this.onVariantChangeBound = (event) => {
      if (event.detail?.variant) {
        this.handleVariantChangeDebounced(event.detail.variant);
      }
    };

    this.onProductRerenderBound = () => {
      setTimeout(() => {
        const currentOptions = this.getCurrentMainProductOptions();
        if (currentOptions.length > 0) {
          this.updateUpsellVariants(currentOptions);
        }
      }, 100);
    };

    document.addEventListener("variant:change", this.onVariantChangeBound);
    document.addEventListener("product:rerender", this.onProductRerenderBound);
  }

  getCurrentMainProductOptions() {
    const mainOptions = [];

    const productDetailVariation = document.querySelector(
      "product-detail-variation"
    );
    if (productDetailVariation) {
      const realVariantInputs = productDetailVariation.querySelectorAll(
        'input[type="radio"]:checked'
      );
      realVariantInputs.forEach((input) => {
        if (!input.name.includes("variant-group-")) {
          const normalized = this.normalizeOptionValue(input.value);
          mainOptions.push(normalized);
        }
      });

      const realSelects = productDetailVariation.querySelectorAll("select");
      realSelects.forEach((select) => {
        if (select.value && !select.closest("product-detail-variant-group")) {
          const normalized = this.normalizeOptionValue(select.value);
          mainOptions.push(normalized);
        }
      });
    }

    return mainOptions;
  }

  handleVariantChangeDebounced(variant) {
    clearTimeout(this.variantChangeTimeout);
    this.variantChangeTimeout = setTimeout(() => {
      this.handleVariantChange(variant);
    }, 100);
  }

  handleVariantChange(variant) {
    if (!variant) return;

    const selectedOptions =
      variant.selected_options || variant.selectedOptions || [];
    if (selectedOptions.length === 0) return;

    const mainOptions = selectedOptions.map((option) => {
      const value = option.value || option.name || option;
      return this.normalizeOptionValue(value);
    });

    this.updateUpsellVariants(mainOptions);
  }

  updateUpsellVariantsLightweight(mainOptions) {
    const upsellItems = this.querySelectorAll("[data-upsell-matcher]");

    upsellItems.forEach((upsellItem) => {
      const currentVariantId = upsellItem.dataset.currentVariantId;
      const matchingVariantId = this.findMatchingVariant(
        upsellItem,
        mainOptions
      );

      if (matchingVariantId && matchingVariantId !== currentVariantId) {
        this.updateUpsellItemCheckboxOnly(upsellItem, matchingVariantId);
      }
    });
  }

  updateUpsellItemCheckboxOnly(upsellItem, newVariantId) {
    const currentCheckbox = upsellItem.querySelector('input[type="checkbox"]');
    if (!currentCheckbox) return;

    currentCheckbox.value = newVariantId;
    upsellItem.dataset.currentVariantId = newVariantId;
  }

  updateUpsellVariants(mainOptions) {
    const upsellItems = this.querySelectorAll("[data-upsell-matcher]");

    upsellItems.forEach((upsellItem) => {
      const currentVariantId = upsellItem.dataset.currentVariantId;
      const matchingVariantId = this.findMatchingVariant(
        upsellItem,
        mainOptions
      );

      if (matchingVariantId && matchingVariantId !== currentVariantId) {
        this.updateUpsellItem(upsellItem, matchingVariantId);
      }
    });
  }

  findMatchingVariant(upsellItem, mainOptions) {
    const availableVariants = upsellItem.dataset.availableVariants;
    if (!availableVariants) return null;

    const variants = availableVariants.split(";").map((entry) => {
      const [optionsStr, variantId] = entry.split(":");
      const options = optionsStr
        ? optionsStr.split("|").filter((o) => o !== "")
        : [];
      return { options, id: variantId };
    });

    const matchingVariant = variants.find((variant) => {
      return variant.options.some((upsellOption) => {
        return mainOptions.some((mainOption) => {
          return upsellOption.includes(mainOption);
        });
      });
    });

    return matchingVariant ? matchingVariant.id : null;
  }

  updateUpsellItem(upsellItem, newVariantId) {
    const currentCheckbox = upsellItem.querySelector('input[type="checkbox"]');
    if (!currentCheckbox) return;

    currentCheckbox.value = newVariantId;
    upsellItem.dataset.currentVariantId = newVariantId;
  }

  setupFormReattach() {
    if (!this.formId) return;

    this.onProductRerenderForFormBound = () => {
      setTimeout(() => {
        this.handleFormSubmitBound = null;
        this.ownsFormSubmit = false;

        this.refreshInputCaches();
        this.syncManagedQuantitiesFromCheckboxes();
        this.setupAddToCartIntercept();
      }, 150);
    };

    document.addEventListener("product:rerender", this.onProductRerenderForFormBound);
  }

  setupAddToCartIntercept() {
    if (!this.formId) return;
    if (this.handleFormSubmitBound) return;
    const stickyFormId = `product-form-sticky-${this.productId}-${this.sectionId}`;
    this.handleFormSubmitBound = (event) => {
      const submittedForm = event.target;
      if (!submittedForm || typeof submittedForm.getAttribute !== "function") return;
      const submittedId = submittedForm.getAttribute("id");
      if (submittedId !== this.formId && submittedId !== stickyFormId) return;
      this.handleFormSubmit(event);
    };
    document.addEventListener("submit", this.handleFormSubmitBound, true);
    this.ownsFormSubmit = true;
  }

  async handleFormSubmit(event) {
    const form = event.target;
    const mainVariantInput =
      form.querySelector("input[data-variant-id-input]") ||
      document.querySelector(`input[data-variant-id-input][form="${this.formId}"]`);
    if (!mainVariantInput?.value) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    // one add at a time (PDP + sticky bar share it): a second click while the first is in flight would add it all twice
    if (Upsell.adding) return;

    const allRoots = document.querySelectorAll(
      `kc-upsell[data-form-id="${this.formId}"]`
    );
    const stateByKey = new Map();
    allRoots.forEach((root) => {
      root.querySelectorAll("input[name='upsell']").forEach((cb) => {
        const dedupeKey = cb.dataset.upsellProductKey || cb.dataset.index;
        if (!dedupeKey) return;

        // Skip checkboxes inside items hidden by CSS (e.g., the desktop
        // product_limit nth-child rule hides items past the visible count).
        // Without this, a checkbox the user CAN'T see/uncheck would still be
        // added to cart — the original bug report.
        if (!Upsell.isCheckboxVisible(cb)) return;

        const existing = stateByKey.get(dedupeKey);
        if (!existing) {
          stateByKey.set(dedupeKey, { allChecked: cb.checked, checkbox: cb });
        } else {
          existing.allChecked = existing.allChecked && cb.checked;
        }
      });
    });
    const checkedCheckboxes = [];
    stateByKey.forEach((entry) => {
      if (entry.allChecked) checkedCheckboxes.push(entry.checkbox);
    });

    const mainVariantId = parseInt(mainVariantInput.value, 10);
    const compareAtInput = form.querySelector(
      'input[name="properties[_compare_at_price]"]'
    );
    const mainItem = { id: mainVariantId, quantity: 1 };
    if (compareAtInput) {
      mainItem.properties = { _compare_at_price: compareAtInput.value };
    }
    const items = [mainItem];

    checkedCheckboxes.forEach((checkbox) => {
      const slotKey = checkbox.dataset.index;
      const root = checkbox.closest("kc-upsell");
      const idInput = root?.querySelector(`input[data-upsell-item="${slotKey}"]`);
      const variantId = parseInt(idInput?.value || checkbox.value, 10);
      if (variantId) items.push({ id: variantId, quantity: 1 });
    });

    // Lets campaign scripts ride along in the SAME add call — kc-bf-offer.js
    // pushes the gift lines that come with the cover here, so sofa, cover and
    // gifts land in the cart together instead of the gifts trailing in a
    // second request. `items` is the live array: listeners push into it.
    document.dispatchEvent(
      new CustomEvent("upsell:collect-items", {
        bubbles: true,
        detail: { items, form, formId: this.formId, mainVariantId },
      })
    );

    const submitButtons = Array.from(form.elements).filter(
      (el) => el.type === "submit"
    );
    submitButtons.forEach((btn) => btn.setAttribute("aria-busy", "true"));
    Upsell.adding = true;
    // set when we are on our way to /cart: the buttons stay busy so a second click can't add everything twice
    let redirecting = false;

    try {
      const root = window.Shopify?.routes?.root || "/";
      const sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(","),
          sections_url: `${root}variants/${mainVariantId}`,
        }),
      });

      const responseJson = await response.json();

      if (response.ok) {
        // theme_add_to_cart straight from the /cart/add.js answer, before anything else; the redirect to /cart waits
        // for its eventCallback (sections/kc-added-to-cart.liquid). The cart events below still run meanwhile.
        const themeAtcPushed =
          window.themeVariables?.settings?.cartType === "page" && typeof window.livomThemeAddToCart === "function";
        if (themeAtcPushed) window.livomThemeAddToCart(responseJson, `${root}cart`);
        const cartContent = await (
          await fetch(`${root}cart.js`)
        ).json();
        cartContent["sections"] = responseJson["sections"];

        form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty("items")
                ? responseJson["items"]
                : [responseJson],
              cart: cartContent,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent: "variant:add", cart: cartContent },
          })
        );

        // There is no cart drawer any more: every cart goes to the review page
        // (Sept 2026). theme.js does this itself for an ordinary product form
        // when cartType is "page", but this handler preventDefault()s that form
        // and posts its own /cart/add.js, so the navigation has to happen here
        // too — otherwise adding from a PDP with upsells gives no feedback at
        // all. The events above are still dispatched first, so cart-count and
        // the campaign listeners see the new cart exactly as they used to.
        if (window.themeVariables?.settings?.cartType === "page") {
          redirecting = true;
          // already pushed above; the redirect comes from livomThemeAddToCart
          if (!themeAtcPushed) window.location.href = `${root}cart`;
          return;
        }
      } else {
        form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: responseJson["description"] },
          })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (e) {
      document.removeEventListener("submit", this.handleFormSubmitBound, true);
      form.requestSubmit();
      document.addEventListener("submit", this.handleFormSubmitBound, true);
    } finally {
      if (!redirecting) {
        submitButtons.forEach((btn) => btn.removeAttribute("aria-busy"));
        Upsell.adding = false;
      }
    }
  }

  normalizeOptionValue(value) {
    return value
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }
}

if (!window.customElements.get("kc-upsell")) {
  window.customElements.define("kc-upsell", Upsell);

  // back from /cart (bfcache): the page comes back exactly as it was left, mid-redirect — free the buttons again
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    Upsell.adding = false;
    document.querySelectorAll('form[action*="/cart/add"] [type="submit"][aria-busy], [type="submit"][form][aria-busy]')
      .forEach((btn) => btn.removeAttribute("aria-busy"));
  });
}
